/**
 * Kalender (Stitch: maestro_kalender): Monats- und Wochenansicht mit Vor/Zurück-Navigation,
 * Wochentagskopf (Mo–So), Events als farbige Chips (Status-Punkt + Titel + "N G. · Ort",
 * Farbe nach Status via PILL_CLASSES), Heute hervorgehoben, Klick -> Event-Detail.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { germanHolidays } from "@maestro/feiertage";
import { useApi } from "../hooks/useApi";
import type { EventRow } from "../ui/types";
import { PILL_CLASSES, eventStatusMeta } from "../ui/format";
import { ErrorNote, Icon, LoadingRows } from "../ui/components";
import { openPrintableDocument } from "../lib/print";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function startOfWeek(d: Date): Date {
  const weekday = (d.getDay() + 6) % 7; // Mo=0
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - weekday);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

type Cell = { date: Date; inMonth: boolean };
type View = "month" | "week";

/** G4: Event-Chip mit Status-Punkt + Titel + separater Zeile "N G. · Ort" (statt Uhrzeit, kein Punkt). */
function EventChip({ e, compact }: { e: EventRow; compact?: boolean }) {
  const meta = eventStatusMeta(e.status); // N-11: unbekannter Status crasht den Chip nicht mehr
  const title = e.occasion ?? e.number ?? "Event";
  const metaLine = [e.guestCount ? `${e.guestCount} G.` : null, e.location].filter(Boolean).join(" · ");
  return (
    <Link
      to={`/veranstaltungen/${e.id}`}
      title={`${title} – ${meta.label}`}
      className="block rounded-md px-1.5 py-1 transition-opacity hover:opacity-80"
    >
      <span className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 ${PILL_CLASSES[meta.tone]}`}>
        <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-current" />
        <span className="truncate font-label-md text-[10.5px] font-semibold leading-tight">{title}</span>
      </span>
      {metaLine && !compact && (
        <span className="mt-0.5 block truncate px-1.5 text-[10px] leading-tight text-secondary-fixed-dim">{metaLine}</span>
      )}
    </Link>
  );
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const { data, loading, error } = useApi<EventRow[]>("/api/events");
  // Datenanreicherung Welle A: fehlende tenant_settings-Zeile -> {data: null} -> "alle Flags
  // false" (nie Fehler, nie Default-an) -- Feiertage bleiben dann einfach ausgeblendet.
  const settingsQ = useApi<{ feiertageEnabled?: boolean; bundesland?: string | null } | null>("/api/settings/payment-terms");
  const feiertageEnabled = settingsQ.data?.feiertageEnabled ?? false;
  const bundesland = settingsQ.data?.bundesland ?? null;
  const [view, setView] = useState<View>("month");
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [weekCursor, setWeekCursor] = useState(() => startOfWeek(new Date()));
  const [printDate, setPrintDate] = useState<string | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);

  // Vor-/Folgemonats-Fueller koennen ins Vor-/Folgejahr reichen -> alle drei Jahre berechnen.
  // Deckt Monats- UND Wochenansicht ab (weekCursor kann nahe Jahreswechsel abweichen).
  const holidaysByDate = useMemo(() => {
    const map = new Map<string, string>();
    if (!feiertageEnabled || !bundesland) return map;
    const years = new Set([cursor.getFullYear(), weekCursor.getFullYear()]);
    for (const year of years) {
      for (const y of [year - 1, year, year + 1]) {
        for (const h of germanHolidays(y, bundesland)) map.set(h.date, h.name);
      }
    }
    return map;
  }, [feiertageEnabled, bundesland, cursor, weekCursor]);

  async function printDay(dateKey: string) {
    setPrintDate(dateKey);
    setPrintError(null);
    try {
      await openPrintableDocument(`/api/events/kitchen-sheet?date=${dateKey}`);
    } catch (err) {
      setPrintError(err instanceof Error ? err.message : String(err));
    } finally {
      setPrintDate(null);
    }
  }

  const byDay = useMemo(() => {
    const map = new Map<string, EventRow[]>();
    for (const e of data ?? []) {
      if (!e.eventDate) continue;
      const key = e.eventDate.slice(0, 10);
      map.set(key, [...(map.get(key) ?? []), e]);
    }
    return map;
  }, [data]);

  // Monats-Zellen: Vormonats-Fueller (Mo-basiert) + Tage des Monats + Folgemonats-Fueller
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthCells: Cell[] = [];
  const firstWeekday = (cursor.getDay() + 6) % 7; // Mo=0
  for (let i = firstWeekday; i > 0; i--) {
    monthCells.push({ date: new Date(year, month, 1 - i), inMonth: false });
  }
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    monthCells.push({ date: new Date(year, month, d), inMonth: true });
  }
  while (monthCells.length % 7 !== 0) {
    const last = monthCells[monthCells.length - 1]!.date;
    monthCells.push({ date: addDays(last, 1), inMonth: false });
  }

  // Wochen-Zellen: 7 Tage ab weekCursor (Mo)
  const weekCells: Cell[] = Array.from({ length: 7 }, (_, i) => ({ date: addDays(weekCursor, i), inMonth: true }));

  const todayKey = ymd(new Date());
  const monthLabel = cursor.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  const weekEnd = addDays(weekCursor, 6);
  const weekLabel =
    weekCursor.getMonth() === weekEnd.getMonth()
      ? `${weekCursor.getDate()}.–${weekEnd.getDate()}. ${weekEnd.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}`
      : `${weekCursor.getDate()}. ${weekCursor.toLocaleDateString("de-DE", { month: "long" })} – ${weekEnd.getDate()}. ${weekEnd.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}`;
  const eventsThisMonth = (data ?? []).filter((e) => e.eventDate && e.eventDate.slice(0, 7) === ymd(new Date(year, month, 1)).slice(0, 7));

  function goPrev() {
    if (view === "month") setCursor(new Date(year, month - 1, 1));
    else setWeekCursor(addDays(weekCursor, -7));
  }
  function goNext() {
    if (view === "month") setCursor(new Date(year, month + 1, 1));
    else setWeekCursor(addDays(weekCursor, 7));
  }
  function goToday() {
    const now = new Date();
    setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
    setWeekCursor(startOfWeek(now));
  }

  return (
    <>
      {/* G3: kein separates Seiten-H1 mehr — der Topbar-Seitentitel ("Kalender") reicht,
          hier nur noch das Monats-/Wochenlabel (22px). */}
      <div className="mb-3.5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="Zurück"
            onClick={goPrev}
            className="grid h-10 w-10 place-items-center rounded-lg border border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant transition-colors hover:text-primary dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
          >
            <Icon name="chevron_left" className="text-[18px]" />
          </button>
          <button
            type="button"
            aria-label="Weiter"
            onClick={goNext}
            className="grid h-10 w-10 place-items-center rounded-lg border border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant transition-colors hover:text-primary dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
          >
            <Icon name="chevron_right" className="text-[18px]" />
          </button>
        </div>
        <h1 className="text-[22px] font-bold capitalize tracking-[-0.02em] text-on-background dark:text-inverse-on-surface">
          {view === "month" ? monthLabel : weekLabel}
        </h1>
        <button
          type="button"
          onClick={goToday}
          className="rounded-lg border border-outline-variant/60 bg-surface-container-lowest px-3 py-1.5 font-label-md text-[11.5px] font-semibold text-on-surface-variant shadow-sm hover:text-primary dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
        >
          Heute
        </button>

        <div className="ml-auto flex flex-wrap items-center gap-3.5">
          {/* Legende — an das echte Status-Set der Chips angeglichen (Tone-Farben aus PILL_CLASSES),
              damit jeder vorkommende Chip-Status erklärt ist. Feiertag mit eigenem Symbol (kein Farbpunkt). */}
          <div className="flex flex-wrap items-center gap-3 text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
            <span className="flex items-center gap-1.5">
              <span className="h-[7px] w-[7px] rounded-full bg-[#31619B]" /> Anfrage
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-[7px] w-[7px] rounded-full bg-[#C98A1B]" /> Angebot gesendet
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-[7px] w-[7px] rounded-full bg-tertiary" /> Bestätigt
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-[7px] w-[7px] rounded-full bg-error" /> Storniert
            </span>
            <span className="flex items-center gap-1.5">
              <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" /> Feiertag
            </span>
          </div>
          {/* G2: Monat/Woche-Toggle */}
          <div className="flex items-center gap-0.5 rounded-[10px] border border-outline-variant/60 bg-surface-container-lowest p-[3px] dark:border-outline/40 dark:bg-inverse-surface">
            <button
              type="button"
              onClick={() => setView("month")}
              className={`rounded-lg px-3.5 py-1.5 font-label-md text-[12px] font-semibold ${
                view === "month" ? "bg-primary text-on-primary" : "text-secondary-fixed-dim"
              }`}
            >
              Monat
            </button>
            <button
              type="button"
              onClick={() => setView("week")}
              className={`rounded-lg px-3.5 py-1.5 font-label-md text-[12px] font-semibold ${
                view === "week" ? "bg-primary text-on-primary" : "text-secondary-fixed-dim"
              }`}
            >
              Woche
            </button>
          </div>
        </div>
      </div>

      {printError && <ErrorNote message={printError} />}
      {error && <ErrorNote message={error} />}
      {loading ? (
        <LoadingRows rows={6} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              {/* Wochentagskopf */}
              <div className="grid grid-cols-7 border-b border-outline-variant/50 bg-surface-container-low/50 dark:border-outline/30 dark:bg-surface-variant/10">
                {WEEKDAYS.map((w) => (
                  <div
                    key={w}
                    className="px-2 py-3 text-center font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim"
                  >
                    {w}
                  </div>
                ))}
              </div>

              {view === "month" ? (
                /* G5: Zellhöhe 120 -> 96px */
                <div className="grid grid-cols-7 gap-px bg-outline-variant/20 dark:bg-outline/20">
                  {monthCells.map(({ date, inMonth }, idx) => {
                    const key = ymd(date);
                    const isToday = key === todayKey;
                    const dayEvents = byDay.get(key) ?? [];
                    const holidayName = holidaysByDate.get(key);
                    // L7: leerer Tag -> Klick legt „Neue Anfrage" mit vorbelegtem Datum an (Route wie Sidebar).
                    const isEmpty = dayEvents.length === 0;
                    const openNewInquiry = () => navigate(`/anfragen?neu=1&datum=${key}`);
                    return (
                      <div
                        key={`${key}-${idx}`}
                        onClick={isEmpty ? openNewInquiry : undefined}
                        onKeyDown={
                          isEmpty
                            ? (ev) => {
                                if (ev.key === "Enter" || ev.key === " ") {
                                  ev.preventDefault();
                                  openNewInquiry();
                                }
                              }
                            : undefined
                        }
                        role={isEmpty ? "button" : undefined}
                        tabIndex={isEmpty ? 0 : undefined}
                        title={isEmpty ? `Neue Anfrage für den ${date.getDate()}. anlegen` : undefined}
                        className={`min-h-[96px] p-2 transition-colors ${isEmpty ? "cursor-pointer" : ""} ${
                          isToday
                            ? "bg-primary-fixed/30 dark:bg-primary-fixed/10"
                            : "bg-surface-container-lowest hover:bg-surface-container-low/40 dark:bg-inverse-surface dark:hover:bg-surface-variant/20"
                        }`}
                      >
                        <div className="mb-1 flex items-center justify-between gap-1">
                          {dayEvents.length > 0 ? (
                            <button
                              type="button"
                              title={`Küchenzettel für ${key} drucken (Sammel-Druck)`}
                              disabled={printDate === key}
                              onClick={() => void printDay(key)}
                              className="rounded p-0.5 text-secondary-fixed-dim transition-colors hover:bg-surface-container-high hover:text-primary disabled:opacity-50 dark:hover:bg-surface-variant/40"
                            >
                              <Icon name="print" className="text-[14px]" />
                            </button>
                          ) : (
                            <span />
                          )}
                          {isToday ? (
                            // L8: „Heute" als gefüllte Badge (Zahl in Kreis) statt Farbpunkt — eindeutig
                            // von den Status-Punkten (u. a. rotes „Storniert") unterscheidbar.
                            <span className="flex items-center gap-1 font-body-sm text-body-sm" title={holidayName}>
                              <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-primary px-1 font-bold text-on-primary">
                                {date.getDate()}
                              </span>
                              {holidayName && <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" />}
                            </span>
                          ) : (
                            <span
                              className={`flex items-center gap-1 font-body-sm text-body-sm ${
                                inMonth ? "text-on-surface dark:text-inverse-on-surface" : "text-secondary-fixed-dim dark:text-outline"
                              }`}
                              title={holidayName}
                            >
                              {date.getDate()}
                              {/* M6: Feiertag mit eigenem Symbol statt bg-tertiary-Punkt (= „Bestätigt"). */}
                              {holidayName && <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" />}
                            </span>
                          )}
                        </div>

                        <div className="space-y-0.5">
                          {dayEvents.slice(0, 2).map((e) => (
                            <EventChip key={e.id} e={e} compact />
                          ))}
                          {dayEvents.length > 2 && (
                            /* CX-Audit 2026-07-17: „+N weitere" war tote Anzeige — Klick wechselt
                               jetzt in die Wochenansicht dieses Tages, wo ALLE Events sichtbar sind. */
                            <button
                              type="button"
                              onClick={() => {
                                setWeekCursor(startOfWeek(date));
                                setView("week");
                              }}
                              className="px-1.5 text-[10px] font-medium text-secondary hover:text-primary hover:underline dark:text-secondary-fixed-dim"
                            >
                              +{dayEvents.length - 2} weitere
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* G2: Wochenansicht — ein Zeilen-Grid, volle Event-Liste je Tag (scrollbar). */
                <div className="grid grid-cols-7 gap-px bg-outline-variant/20 dark:bg-outline/20">
                  {weekCells.map(({ date }, idx) => {
                    const key = ymd(date);
                    const isToday = key === todayKey;
                    const dayEvents = byDay.get(key) ?? [];
                    const holidayName = holidaysByDate.get(key);
                    // L7: leerer Tag -> Klick legt „Neue Anfrage" mit vorbelegtem Datum an.
                    const isEmpty = dayEvents.length === 0;
                    const openNewInquiry = () => navigate(`/anfragen?neu=1&datum=${key}`);
                    return (
                      <div
                        key={`${key}-${idx}`}
                        onClick={isEmpty ? openNewInquiry : undefined}
                        onKeyDown={
                          isEmpty
                            ? (ev) => {
                                if (ev.key === "Enter" || ev.key === " ") {
                                  ev.preventDefault();
                                  openNewInquiry();
                                }
                              }
                            : undefined
                        }
                        role={isEmpty ? "button" : undefined}
                        tabIndex={isEmpty ? 0 : undefined}
                        title={isEmpty ? `Neue Anfrage für den ${date.getDate()}. anlegen` : undefined}
                        className={`flex min-h-[420px] flex-col p-2 transition-colors ${isEmpty ? "cursor-pointer" : ""} ${
                          isToday
                            ? "bg-primary-fixed/30 dark:bg-primary-fixed/10"
                            : "bg-surface-container-lowest dark:bg-inverse-surface"
                        }`}
                      >
                        <div className="mb-1.5 flex items-center justify-between gap-1">
                          {dayEvents.length > 0 ? (
                            <button
                              type="button"
                              title={`Küchenzettel für ${key} drucken (Sammel-Druck)`}
                              disabled={printDate === key}
                              onClick={() => void printDay(key)}
                              className="rounded p-0.5 text-secondary-fixed-dim transition-colors hover:bg-surface-container-high hover:text-primary disabled:opacity-50 dark:hover:bg-surface-variant/40"
                            >
                              <Icon name="print" className="text-[14px]" />
                            </button>
                          ) : (
                            <span />
                          )}
                          {isToday ? (
                            // L8: „Heute" als gefüllte Badge (siehe Monatsansicht).
                            <span className="flex items-center gap-1 font-body-sm text-body-sm" title={holidayName}>
                              <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-primary px-1 font-bold text-on-primary">
                                {date.getDate()}
                              </span>
                              {holidayName && <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" />}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface" title={holidayName}>
                              {date.getDate()}
                              {/* M6: Feiertag mit eigenem Symbol statt bg-tertiary-Punkt. */}
                              {holidayName && <Icon name="flag" className="text-[13px] leading-none text-secondary-fixed-dim" />}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 space-y-1 overflow-y-auto">
                          {dayEvents.map((e) => (
                            <EventChip key={e.id} e={e} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {!loading && view === "month" && (
        <p className="mt-4 flex items-center gap-1.5 text-[11.5px] text-secondary-fixed-dim">
          <Icon name="info" className="text-[14px]" />
          {eventsThisMonth.length} Veranstaltung{eventsThisMonth.length === 1 ? "" : "en"} im {monthLabel.split(" ")[0]} · Klick auf einen Chip öffnet das Event-Detail
        </p>
      )}
    </>
  );
}
